import React, { useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../utils/isSaudiBrand';
import QawaemPageTitleWithFilters from './QawaemPageTitleWithFilters';
import QawaemStatsCard from './QawaemStatsCard';
import QawaemTableToolbarSearch from './QawaemTableToolbarSearch';
import QawaemTableCardsViewToggle from './QawaemTableCardsViewToggle';
import QawaemImportExportToolbar from './QawaemImportExportToolbar';

/** Standard 4-card KPI grid — matches المنتجات layout. */
export const QAWAEM_KPI_GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6';

/** Card view: two cards per row on large screens. */
export const QAWAEM_CARD_VIEW_GRID_CLASS = 'p-6 grid grid-cols-1 lg:grid-cols-2 gap-4';
export const QAWAEM_CARD_VIEW_ITEM_CLASS = 'h-full min-w-0';
export const QAWAEM_CARD_VIEW_INNER_GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 gap-4';

/** Keep at most 4 KPI cards for list pages. */
export function sliceQawaemStats(stats = [], max = 4) {
  return stats.slice(0, max);
}

/**
 * Shared list-page chrome matching المنتجات (Products):
 * title row filters (bulk actions · search in cards mode), 4 KPI cards,
 * new + import/export actions, glass table shell — no inline thead filters.
 */
export default function QawaemErpListPageShell({
  title,
  subtitle,
  icon: Icon,
  stats = [],
  viewMode = 'table',
  onViewModeChange,
  filterActions = null,
  searchTerm = '',
  onSearchChange,
  searchPlaceholder,
  columns,
  visibleColumnCount,
  totalColumnCount,
  onCreate,
  createLabel,
  importExport,
  headerActions = null,
  children,
  footer = null,
  className = '',
}) {
  const { t } = useTranslation();
  const qawaem = isSaudiBrand();
  const kpiCards = sliceQawaemStats(stats, 4);

  useEffect(() => {
    if (viewMode === 'table' && onSearchChange && String(searchTerm || '').trim()) {
      onSearchChange('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!qawaem) {
    return (
      <div className={`space-y-6 employees-container ${className}`}>
        <div className="flex justify-between items-start list-toolbar-mobile-layout">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              {Icon ? <Icon className="h-8 w-8 text-blue-400" /> : null}
              {title}
            </h1>
            {subtitle ? <p className="text-white/60 mt-2">{subtitle}</p> : null}
          </div>
          {onCreate ? (
            <button type="button" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2" onClick={onCreate}>
              <Plus className="h-5 w-5" />
              <span>{createLabel}</span>
            </button>
          ) : null}
        </div>
        {children}
        {footer}
      </div>
    );
  }

  return (
    <div className={`space-y-6 employees-container ${className}`}>
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          <QawaemPageTitleWithFilters
            titleSuffix={onViewModeChange ? <QawaemTableCardsViewToggle value={viewMode} onChange={onViewModeChange} /> : null}
            filters={
              <>
                {filterActions}
                {onSearchChange && viewMode === 'cards' ? (
                  <QawaemTableToolbarSearch
                    viewMode={viewMode}
                    value={searchTerm}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder || t('common.search', { defaultValue: 'بحث...' })}
                  />
                ) : null}
              </>
            }
          >
            <h1 className="text-3xl font-bold text-forest-900 flex items-center gap-3">
              {Icon ? <Icon className="page-title-icon page-title-icon--bronze h-8 w-8" /> : null}
              {title}
            </h1>
          </QawaemPageTitleWithFilters>
          {subtitle ? <p className="page-section-desc">{subtitle}</p> : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {headerActions}
          {onCreate ? (
            <button type="button" className="btn-stat-green btn-header-compact shrink-0" onClick={onCreate}>
              <span className="hidden sm:inline">{createLabel}</span>
              <Plus className="h-4 w-4" />
            </button>
          ) : null}
          {importExport ? <QawaemImportExportToolbar compact {...importExport} /> : null}
        </div>
      </div>

      {kpiCards.length ? (
        <div className={QAWAEM_KPI_GRID_CLASS}>
          {kpiCards.map((stat, i) => (
            <QawaemStatsCard
              key={stat.key || stat.label || i}
              label={stat.label}
              value={stat.value}
              icon={stat.icon}
              variant={stat.variant || (i % 2 === 0 ? 'copper' : 'forest')}
              delay={i * 0.08}
              className={stat.className}
            />
          ))}
        </div>
      ) : null}

      <div className="glass-panel glass-table-shell overflow-visible">
        {children}
        {footer}
      </div>
    </div>
  );
}
